import type { ColumnsType } from "antd/es/table/interface";
import { Link } from "react-router";
import { buildColumns } from "@/views/Data/TrialIssues/utils";
import SignalScoreCell from "./SignalScoreCell";
import {
  COLUMN_WIDTHS,
  FIXED_COLUMNS,
  HIDDEN_COLUMNS,
  SEARCHABLE_COLUMNS,
  SIGNAL_COLUMN_ORDER,
  TITLE_MAPPING,
  type SignalTableRow,
} from "./utils";

const BAND_COLOR: Record<string, string> = {
  Red: "#D9534F",
  Yellow: "#C2A34A",
  Green: "#53A87A",
};

const NO_FILTER_COLUMNS = new Set([
  "current_score",
  "linked_risks",
  "linked_breaches",
]);

const BREACH_STATUS_FILTERS = [
  {
    text: (
      <span className="inline-flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full bg-[#D9534F] inline-block" />
        <span>Breached</span>
      </span>
    ),
    value: "Red",
  },
  {
    text: (
      <span className="inline-flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full bg-[#C2A34A] inline-block" />
        <span>At Risk</span>
      </span>
    ),
    value: "Yellow",
  },
  {
    text: (
      <span className="inline-flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full bg-[#53A87A] inline-block" />
        <span>On Track</span>
      </span>
    ),
    value: "Green",
  },
];

export const createSignalColumns = (
  rows: SignalTableRow[],
): ColumnsType<SignalTableRow> => {
  const cols = buildColumns({
    rows,
    columnOrder: SIGNAL_COLUMN_ORDER,
    titleMapping: TITLE_MAPPING,
    columnWidths: COLUMN_WIDTHS,
    searchableColumns: SEARCHABLE_COLUMNS,
    fixedColumns: FIXED_COLUMNS,
    hiddenColumns: HIDDEN_COLUMNS,
    customRender: (header, text, record) => {
      if (header === "breach_status") {
        const band = record.band as string;
        const color = BAND_COLOR[band] ?? "#556C75";
        const label =
          band === "Red"
            ? "Breached"
            : band === "Yellow"
              ? "At Risk"
              : "On Track";
        return (
          <span className="inline-flex items-center gap-1.5">
            <span
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: color }}
            />
            <span style={{ color }}>{label}</span>
          </span>
        );
      }

      if (header === "current_score") {
        return (
          <SignalScoreCell
            score={text as string | number | boolean | null}
            band={record.band as string | number | boolean | null}
          />
        );
      }

      if (header === "linked_risks" && typeof text === "string" && text) {
        return (
          <Link
            className="text-[#06709B]"
            to={`../risks?openRisk=${text}`}
            relative="path"
            onClick={(e) => e.stopPropagation()}
          >
            {text}
          </Link>
        );
      }

      if (header === "linked_breaches" && typeof text === "string" && text) {
        return (
          <Link
            className="text-[#06709B]"
            to={`../issues?openIssue=${text}`}
            relative="path"
            onClick={(e) => e.stopPropagation()}
          >
            {text}
          </Link>
        );
      }

      return undefined;
    },
  }) as ColumnsType<SignalTableRow>;

  return cols.map((col) => {
    const key = col.key as string;

    if (key === "breach_status") {
      return {
        ...col,
        filters: BREACH_STATUS_FILTERS,
        onFilter: (value, record) => (record as SignalTableRow).band === value,
      };
    }

    if (NO_FILTER_COLUMNS.has(key)) {
      return { ...col, filters: undefined, onFilter: undefined };
    }

    return col;
  });
};
